// FeedbackWidget — floating button + slide-out drawer for collecting
// feedback from any page. Mounted once in DashboardLayout so every
// authenticated route gets it for free.
//
// Interaction model:
//   - Bottom-right circular button with an open-count badge
//   - Click opens a 380px drawer sliding in from the right
//   - Composer at top (textarea + page-URL display + submit)
//   - Filter tabs (Open / Resolved / All)
//   - List of items, newest first, with author, page, timestamp, resolve/delete
//
// The drawer uses an explicit high z-index so it sits above all other
// page chrome but stays below modals (if any). Escape closes the drawer.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  MessageSquare, X, Send, Loader2, CheckCircle2, Circle,
  Trash2, AlertCircle,
} from 'lucide-react'
import {
  feedback as feedbackApi,
  type FeedbackItem,
  type FeedbackStatus,
} from '../api'
import { useAuth } from '../../core/auth'

type TabKey = 'open' | 'resolved' | 'all'

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime()
    const now = Date.now()
    const diff = Math.max(0, now - then)
    const m = Math.floor(diff / 60_000)
    if (m < 1) return 'just now'
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    const d = Math.floor(h / 24)
    if (d < 7) return `${d}d ago`
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    })
  } catch {
    return iso
  }
}

export default function FeedbackWidget() {
  const { user } = useAuth()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [openCount, setOpenCount] = useState(0)
  const [loadingList, setLoadingList] = useState(false)
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabKey>('open')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const currentPage = location.pathname + location.search

  // Fetch open-count for the badge. Refreshed whenever the drawer opens
  // or after a mutation. Light enough to not need polling.
  const reloadCount = useCallback(async () => {
    try {
      const r = await feedbackApi.count()
      setOpenCount(r.open_count)
    } catch {
      // silent — badge is best-effort
    }
  }, [])

  useEffect(() => {
    if (!user) return
    reloadCount()
  }, [user, reloadCount])

  // Load the list for the active tab. Refreshed when the drawer opens or
  // when the user flips between Open / Resolved / All.
  const reloadList = useCallback(async () => {
    if (!open) return
    setLoadingList(true)
    setError(null)
    try {
      const list = await feedbackApi.list({ status: tab })
      setItems(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feedback')
    } finally {
      setLoadingList(false)
    }
  }, [open, tab])

  useEffect(() => {
    reloadList()
  }, [reloadList])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Focus the textarea when the drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 50)
    }
  }, [open])

  const handleSubmit = useCallback(async () => {
    const trimmed = body.trim()
    if (!trimmed) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await feedbackApi.create(trimmed, currentPage)
      setBody('')
      setItems((prev) => [created, ...prev])
      await reloadCount()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }, [body, currentPage, reloadCount])

  const toggleStatus = useCallback(
    async (item: FeedbackItem) => {
      const next: FeedbackStatus = item.status === 'open' ? 'resolved' : 'open'
      try {
        const updated = await feedbackApi.setStatus(item.id, next)
        setItems((prev) => prev.map((p) => (p.id === item.id ? updated : p)))
        await reloadCount()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update')
      }
    },
    [reloadCount],
  )

  const removeItem = useCallback(
    async (item: FeedbackItem) => {
      if (!confirm('Delete this feedback?')) return
      try {
        await feedbackApi.delete(item.id)
        setItems((prev) => prev.filter((p) => p.id !== item.id))
        await reloadCount()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete')
      }
    },
    [reloadCount],
  )

  // Don't render for unauthenticated users (defence in depth —
  // RequireAuth usually handles this already)
  if (!user) return null

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open feedback"
          className="fixed bottom-5 right-5 z-[9998] flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-fg shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          <MessageSquare className="h-5 w-5" />
          {openCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-bg-primary px-1 text-[10px] font-bold text-accent border-2 border-accent">
              {openCount > 99 ? '99+' : openCount}
            </span>
          )}
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[9997] bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Drawer */}
      <aside
        className={`fixed right-0 top-0 z-[9999] flex h-full w-full max-w-[420px] flex-col bg-bg-secondary border-l border-border shadow-2xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">Feedback</h2>
            <p className="text-2xs text-text-muted">
              Tell us what's working and what isn't — we'll see it right away.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 text-text-muted hover:bg-bg-hover hover:text-text-primary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Composer */}
        <div className="border-b border-border p-3">
          <div className="mb-2 text-2xs text-text-muted">
            On page: <span className="font-mono text-text-secondary">{currentPage}</span>
          </div>
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            placeholder="What needs changing here? Anything you'd like improved, fixed, or added..."
            rows={4}
            className="w-full resize-y rounded-md border border-border bg-bg-tertiary px-3 py-2 text-xs text-text-primary placeholder-text-muted outline-none focus:ring-1 focus:ring-accent"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-2xs text-text-muted">
              Cmd/Ctrl + Enter to submit
            </span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !body.trim()}
              className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Submit
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 border-b border-border px-3 pt-2">
          {(['open', 'resolved', 'all'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-t-md px-3 py-1.5 text-2xs font-medium transition-colors ${
                tab === t
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-negative/30 bg-negative/10 px-3 py-2 text-xs text-negative">
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-auto hover:text-text-primary"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3">
          {loadingList ? (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-2xs text-text-muted">
              {tab === 'open'
                ? 'No open feedback yet. Be the first.'
                : tab === 'resolved'
                ? 'No resolved items.'
                : 'No feedback yet.'}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <article
                  key={item.id}
                  className={`rounded-lg border p-3 transition-colors ${
                    item.status === 'resolved'
                      ? 'border-border bg-bg-tertiary/50 opacity-70'
                      : 'border-border bg-bg-tertiary'
                  }`}
                >
                  <header className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-text-primary">
                          {item.author_name || `User ${item.user_id}`}
                        </span>
                        <span className="text-2xs text-text-muted">
                          · {formatRelative(item.created_at)}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate font-mono text-2xs text-text-muted">
                        {item.page_url}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleStatus(item)}
                        className={`rounded-md p-1 transition-colors ${
                          item.status === 'resolved'
                            ? 'text-positive hover:bg-bg-hover'
                            : 'text-text-muted hover:bg-bg-hover hover:text-positive'
                        }`}
                        title={
                          item.status === 'resolved'
                            ? 'Reopen'
                            : 'Mark resolved'
                        }
                      >
                        {item.status === 'resolved' ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : (
                          <Circle className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => removeItem(item)}
                        className="rounded-md p-1 text-text-muted hover:bg-negative/10 hover:text-negative"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </header>
                  <p className="whitespace-pre-wrap text-xs text-text-primary">
                    {item.body}
                  </p>
                  {item.status === 'resolved' && item.resolver_name && (
                    <p className="mt-2 text-2xs text-text-muted">
                      Resolved by {item.resolver_name}
                      {item.resolved_at ? ` · ${formatRelative(item.resolved_at)}` : ''}
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
